import React, { useMemo, useState } from 'react';
import { Table } from '../../Component/Table';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  Button,
  Tag,
  Popconfirm,
  message,
  Avatar,
  Typography,
  Spin,
  Modal,
  Descriptions,
  Table as AntTable,
  Image,
  Tooltip,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  PlusOutlined,
  GiftOutlined,
  CalendarOutlined,
  ShoppingOutlined,
  TeamOutlined,
  TagOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import 'dayjs/locale/ar';
import Api from '../../Network';
import { usePermissions } from '../../contexts/PermissionContext';
import { resolveStorageUrl } from './helpers';
import { AnimatedContainer } from '../../Component/ui';
import './style.scss';

const { Text, Title } = Typography;

type OffersSummary = {
  total_offers?: number;
  active_offers?: number;
  expired_offers?: number;
  total_usage?: number;
  orders_with_offer?: number;
  active_special_prices_sum?: number;
  nearest_active_expiry?: string | null;
};

type OfferDetails = {
  id: number;
  title?: string;
  image?: string | null;
  image_url?: string | null;
  special_price?: number | string;
  special_price_tax?: number | string | null;
  special_price_after_tax?: number | string | null;
  expiry_date?: string;
  status?: string;
  is_expired?: boolean;
  usage_count?: number;
  created_at?: string;
  updated_at?: string;
  products?: Array<{
    id: number;
    name_ar?: string;
    name_en?: string;
    picture?: string | null;
    selling_price?: number | string | null;
    pivot?: {
      quantity?: number | string | null;
      unit_price?: number | string | null;
    };
  }>;
  customers?: Array<{
    id: number;
    name?: string;
    phone_number?: string;
    organization?: string | null;
  }>;
  orders?: Array<{
    id: number;
    reference?: string;
    status?: string;
    shipping_status?: string;
    created_at?: string;
    customer?: { name?: string; phone_number?: string };
  }>;
};

/** Align with API appended fields on Offer model; fallback if omitted. */
function offerTaxBreakdownFromDetails(offer: OfferDetails): { tax: number; afterTax: number } {
  const base = Number(offer.special_price || 0);
  const taxFromApi =
    offer.special_price_tax !== undefined && offer.special_price_tax !== null
      ? Number(offer.special_price_tax)
      : Math.round(base * 0.15 * 100) / 100;
  const afterFromApi =
    offer.special_price_after_tax !== undefined && offer.special_price_after_tax !== null
      ? Number(offer.special_price_after_tax)
      : Math.round((base + taxFromApi) * 100) / 100;
  return { tax: taxFromApi, afterTax: afterFromApi };
}

function formatMoney(value: number | string | null | undefined): string {
  return Number(value || 0).toFixed(2);
}

function formatDateTime(value?: string | null): string {
  return value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '—';
}

const Offers: React.FC = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const isRtl = Boolean(i18n.language?.startsWith('ar'));
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState<OfferDetails | null>(null);

  const selectedOfferTax = useMemo(() => {
    if (!selectedOffer) {
      return { tax: 0, afterTax: 0 };
    }
    return offerTaxBreakdownFromDetails(selectedOffer);
  }, [selectedOffer]);

  const { data: summaryResp, isLoading: summaryLoading } = useQuery({
    queryKey: ['offers-summary'],
    queryFn: () => Api.get('/api/admin/offers/summary'),
    refetchOnWindowFocus: false,
    retry: false,
  });

  const summary = useMemo(() => {
    const raw = (summaryResp as { data?: OffersSummary } | undefined)?.data;
    return raw ?? {};
  }, [summaryResp]);

  const activeCount = Number(summary.active_offers ?? 0);

  const activeBadgeText = useMemo(() => {
    if (activeCount === 0) {
      return t('offers.heroBadgeActive_zero');
    }
    if (activeCount === 1) {
      return t('offers.heroBadgeActive_one');
    }
    return t('offers.heroBadgeActive_other', { count: activeCount });
  }, [activeCount, t]);

  const deleteOfferMutation = useMutation({
    mutationFn: (offerId: number) => Api.delete(`/api/admin/offers/${offerId}`),
    onSuccess: () => {
      message.success(t('offers.deleteSuccess', 'Offer deleted successfully'));
      queryClient.invalidateQueries({ queryKey: ['/api/admin/offers'] });
      queryClient.invalidateQueries({ queryKey: ['offers-summary'] });
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.msg || t('offers.deleteError', 'Error deleting offer');
      message.error(errorMessage);
    },
  });

  const handleDelete = (offerId: number) => {
    deleteOfferMutation.mutate(offerId);
  };

  const handleEdit = (record: any) => {
    history.push(`/offers/${record.id}/edit`);
  };

  const handleAddNew = () => {
    history.push('/offers/new');
  };

  const handleViewDetails = async (offerId: number) => {
    setDetailsOpen(true);
    setDetailsLoading(true);
    try {
      const response = await Api.get(`/api/admin/offers/${offerId}`);
      setSelectedOffer(response?.data ?? null);
    } catch (error) {
      console.error('Failed to fetch offer details', error);
      message.error(t('messages.operationFailed', 'Operation failed'));
      setDetailsOpen(false);
      setSelectedOffer(null);
    } finally {
      setDetailsLoading(false);
    }
  };

  const canCreate = hasPermission('create_offer') || hasPermission('offers.create');
  const canUpdate = hasPermission('update_offer') || hasPermission('offers.update');
  const canDelete = hasPermission('delete_offer') || hasPermission('offers.delete');

  const renderStatusTag = (status: string, isExpired?: boolean) => {
    const effective = isExpired ? 'Expired' : status || 'Active';
    const colorMap: Record<string, string> = {
      Active: 'green',
      Inactive: 'default',
      Expired: 'red',
    };
    const labelMap: Record<string, string> = {
      Active: t('offers.status.active', 'Active'),
      Inactive: t('offers.status.inactive', 'Inactive'),
      Expired: t('offers.status.expired', 'Expired'),
    };
    const slug = effective.toLowerCase();
    return (
      <Tag
        color={colorMap[effective] || 'default'}
        className={`offer-status-tag offer-status-tag--${slug}`}
      >
        {labelMap[effective] || effective}
      </Tag>
    );
  };

  const offerImageUrl = selectedOffer
    ? resolveStorageUrl(selectedOffer.image_url || selectedOffer.image)
    : '';

  return (
    <AnimatedContainer
      className={`offers-page${isRtl ? ' offers-page--rtl' : ''}`}
      variant="fadeUp"
    >
      <Spin spinning={summaryLoading}>
        <section className="offers-page-hero" aria-labelledby="offers-hero-heading">
          <div className="offers-page-hero__decor" aria-hidden />
          <div className="offers-page-hero__inner">
            <div className="offers-page-hero__top">
              <div className="offers-page-hero__center">
                <div className="offers-page-hero__icon-wrap" aria-hidden>
                  <TagOutlined />
                </div>
                <Title level={2} id="offers-hero-heading" className="offers-page-hero__title">
                  {t('offers.heroTitle')}
                </Title>
                <p className="offers-page-hero__subtitle">{t('offers.heroSubtitle')}</p>
              </div>
            </div>

            <div className="offers-page-hero__toolbar">
              <div className="offers-page-hero__badges">
                <span className="offers-page-hero__badge offers-page-hero__badge--active">
                  <span className="offers-page-hero__badge-dot offers-page-hero__badge-dot--cyan" />
                  {activeBadgeText}
                </span>
              </div>
              {canCreate && (
                <Button
                  type="primary"
                  size="large"
                  icon={<PlusOutlined />}
                  className="offers-page-hero__cta"
                  onClick={handleAddNew}
                >
                  {t('offers.addNew')}
                </Button>
              )}
            </div>

            <div className="offers-page-hero__metrics" aria-label={t('offers.heroMetricsAria')}>
              <span className="offers-page-hero__metric">
                {t('offers.stats.totalOffers')}: <strong>{summary.total_offers ?? 0}</strong>
              </span>
              <span className="offers-page-hero__metric">
                {t('offers.stats.expiredOffers')}: <strong>{summary.expired_offers ?? 0}</strong>
              </span>
              <span className="offers-page-hero__metric">
                {t('offers.stats.totalUsage')}: <strong>{summary.total_usage ?? 0}</strong>
              </span>
              <span className="offers-page-hero__metric">
                {t('offers.stats.ordersWithOffer')}:{' '}
                <strong>{summary.orders_with_offer ?? 0}</strong>
              </span>
            </div>
          </div>
        </section>
      </Spin>

      <div id="offers-data-table">
        <Table
          containerClassName="offers-list-table"
          tableQueryKey="/api/admin/offers"
          showSearch={true}
          hideSearch={false}
          showDeleteSwitch={false}
          hideFilters={true}
          rowKey="id"
          createPermission="offers.create"
          tableFilter={{
            filters: {},
            paging: { page: 1, pageSize: 20 },
            sorting: {},
          }}
          columns={[
            {
              title: t('offers.columns.image', 'Image'),
              dataIndex: 'image',
              key: 'image',
              width: 80,
              render: (image: string, record: any) => {
                const url = resolveStorageUrl(record?.image_url || image);
                return url ? (
                  <Avatar
                    src={url}
                    shape="square"
                    size={56}
                    className="offer-cover offer-cover--list"
                    alt=""
                  />
                ) : (
                  <Avatar
                    shape="square"
                    size={56}
                    icon={<GiftOutlined />}
                    className="offer-cover offer-cover--list offer-cover--placeholder"
                    alt=""
                  />
                );
              },
            },
            {
              title: t('offers.columns.id', 'ID'),
              dataIndex: 'id',
              key: 'id',
              width: 72,
              render: (id: number) => <span className="offer-id">#{id}</span>,
            },
            {
              title: t('offers.columns.title', 'Title'),
              dataIndex: 'title',
              key: 'title',
              ellipsis: true,
              render: (title: string) => (
                <Text strong className="offers-table-title-cell">
                  {title?.trim() ? title : '—'}
                </Text>
              ),
            },
            {
              title: t('offers.columns.specialPrice', 'Special Price'),
              dataIndex: 'special_price',
              key: 'special_price',
              render: (price: any) => (
                <span className="offer-price">
                  {Number(price || 0).toFixed(2)} {t('common.SAR', 'SAR')}
                </span>
              ),
            },
            {
              title: t('offers.columns.expiryDate', 'Expiry Date'),
              dataIndex: 'expiry_date',
              key: 'expiry_date',
              render: (date: string) => (
                <span className="offers-cell-expiry">
                  <CalendarOutlined className="offers-cell-expiry__icon" aria-hidden />
                  <span className="offers-cell-expiry__text">
                    {date ? dayjs(date).format('YYYY-MM-DD') : '—'}
                  </span>
                </span>
              ),
            },
            {
              title: t('offers.columns.products', 'Products'),
              dataIndex: 'products_count',
              key: 'products_count',
              render: (count: number, record: any) => (
                <span className="offers-stat-pill offers-stat-pill--products">
                  <ShoppingOutlined className="offers-stat-pill__icon" aria-hidden />
                  <span className="offers-stat-pill__value">
                    {count ?? record?.products?.length ?? 0}
                  </span>
                </span>
              ),
            },
            {
              title: t('offers.columns.customers', 'Customers'),
              dataIndex: 'customers_count',
              key: 'customers_count',
              render: (count: number, record: any) => (
                <span className="offers-stat-pill offers-stat-pill--customers">
                  <TeamOutlined className="offers-stat-pill__icon" aria-hidden />
                  <span className="offers-stat-pill__value">
                    {count ?? record?.customers?.length ?? 0}
                  </span>
                </span>
              ),
            },
            {
              title: t('offers.columns.usageCount', 'Times used'),
              dataIndex: 'usage_count',
              key: 'usage_count',
              width: 110,
              align: 'center' as const,
              render: (n: number) => (
                <span className="offers-metric-num">{Number(n ?? 0).toLocaleString()}</span>
              ),
            },
            {
              title: t('offers.columns.ordersCount', 'Orders'),
              dataIndex: 'orders_count',
              key: 'orders_count',
              width: 100,
              align: 'center' as const,
              render: (n: number) => (
                <span className="offers-metric-num">{Number(n ?? 0).toLocaleString()}</span>
              ),
            },
            {
              title: t('offers.columns.status', 'Status'),
              dataIndex: 'status',
              key: 'status',
              width: 110,
              render: (status: string, record: any) => renderStatusTag(status, record?.is_expired),
            },
            {
              title: t('common.actions', 'Actions'),
              key: 'actions',
              width: 140,
              align: 'center' as const,
              render: (_: any, record: any) => (
                <div className="offers-row-actions">
                  {canUpdate && (
                    <Tooltip title={t('common.view', 'View')}>
                      <Button
                        type="default"
                        size="small"
                        className="offers-table-btn"
                        icon={<EyeOutlined />}
                        onClick={() => handleViewDetails(record.id)}
                        aria-label={t('common.view', 'View')}
                      />
                    </Tooltip>
                  )}
                  {canUpdate && (
                    <Tooltip title={t('common.edit', 'Edit')}>
                      <Button
                        type="default"
                        size="small"
                        className="offers-table-btn offers-table-btn--edit"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                        aria-label={t('common.edit', 'Edit')}
                      />
                    </Tooltip>
                  )}
                  {canDelete && (
                    <Popconfirm
                      title={t(
                        'offers.deleteConfirm',
                        'Are you sure you want to delete this offer?'
                      )}
                      description={t('offers.deleteWarning', 'This action cannot be undone.')}
                      onConfirm={() => handleDelete(record.id)}
                      okText={t('common.yes', 'Yes')}
                      cancelText={t('common.no', 'No')}
                    >
                      <Tooltip title={t('common.delete', 'Delete')}>
                        <Button
                          type="primary"
                          danger
                          size="small"
                          className="offers-table-btn offers-table-btn--delete"
                          icon={<DeleteOutlined />}
                          aria-label={t('common.delete', 'Delete')}
                        />
                      </Tooltip>
                    </Popconfirm>
                  )}
                </div>
              ),
            },
          ]}
          rowClassName={() => 'offers-table-row'}
          scroll={{ x: 1280 }}
        />
      </div>

      <Modal
        open={detailsOpen}
        onCancel={() => {
          setDetailsOpen(false);
          setSelectedOffer(null);
        }}
        footer={null}
        width={980}
        title={t('common.view', 'View')}
      >
        <Spin spinning={detailsLoading}>
          {selectedOffer && (
            <div className="offer-details-modal">
              <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
                <Descriptions.Item label={t('offers.columns.image', 'Image')} span={2}>
                  {offerImageUrl ? (
                    <Image
                      src={offerImageUrl}
                      alt={selectedOffer.title || 'offer'}
                      width={180}
                      style={{ borderRadius: 12, objectFit: 'cover' }}
                    />
                  ) : (
                    '—'
                  )}
                </Descriptions.Item>
                <Descriptions.Item label={t('offers.columns.id', 'ID')}>
                  #{selectedOffer.id}
                </Descriptions.Item>
                <Descriptions.Item label={t('offers.columns.title', 'Title')}>
                  {selectedOffer.title || '—'}
                </Descriptions.Item>
                <Descriptions.Item label={t('offers.columns.specialPrice', 'Special Price')}>
                  {formatMoney(selectedOffer.special_price)} {t('common.SAR', 'SAR')}
                </Descriptions.Item>
                <Descriptions.Item label={t('offers.form.specialPriceTotalTax', 'Total tax (VAT)')}>
                  {formatMoney(selectedOfferTax.tax)} {t('common.SAR', 'SAR')}
                </Descriptions.Item>
                <Descriptions.Item label={t('offers.form.specialPriceAfterTax', 'Total after tax')}>
                  {formatMoney(selectedOfferTax.afterTax)} {t('common.SAR', 'SAR')}
                </Descriptions.Item>
                <Descriptions.Item label={t('offers.columns.expiryDate', 'Expiry Date')}>
                  {selectedOffer.expiry_date
                    ? dayjs(selectedOffer.expiry_date).format('YYYY-MM-DD')
                    : '—'}
                </Descriptions.Item>
                <Descriptions.Item label={t('offers.columns.status', 'Status')}>
                  {renderStatusTag(selectedOffer.status ?? 'Inactive', selectedOffer?.is_expired)}
                </Descriptions.Item>
                <Descriptions.Item label={t('offers.columns.usageCount', 'Times used')}>
                  {Number(selectedOffer.usage_count ?? 0).toLocaleString()}
                </Descriptions.Item>
                <Descriptions.Item label={t('offers.columns.products', 'Products')}>
                  {selectedOffer.products?.length ?? 0}
                </Descriptions.Item>
                <Descriptions.Item label={t('offers.columns.customers', 'Customers')}>
                  {selectedOffer.customers?.length ?? 0}
                </Descriptions.Item>
                <Descriptions.Item label={t('offers.columns.ordersCount', 'Orders')}>
                  {selectedOffer.orders?.length ?? 0}
                </Descriptions.Item>
                <Descriptions.Item label={t('common.createdAt', 'Created At')}>
                  {formatDateTime(selectedOffer.created_at)}
                </Descriptions.Item>
                <Descriptions.Item label={t('common.updatedAt', 'Updated At')}>
                  {formatDateTime(selectedOffer.updated_at)}
                </Descriptions.Item>
                <Descriptions.Item label={t('offers.form.imageTitle', 'Offer image path')} span={2}>
                  {selectedOffer.image || selectedOffer.image_url || '—'}
                </Descriptions.Item>
              </Descriptions>

              <Title level={5} style={{ marginBottom: 10 }}>
                {t('offers.columns.products', 'Offer Items')} ({selectedOffer.products?.length ?? 0}
                )
              </Title>
              <AntTable
                dataSource={selectedOffer.products || []}
                rowKey="id"
                pagination={false}
                size="small"
                scroll={{ x: 760 }}
                style={{ marginBottom: 16 }}
                columns={[
                  {
                    title: t('offers.columns.image', 'Image'),
                    dataIndex: 'picture',
                    key: 'picture',
                    width: 90,
                    render: (value: string | null | undefined) => {
                      const url = resolveStorageUrl(value);
                      return url ? (
                        <Avatar src={url} shape="square" size={48} />
                      ) : (
                        <Avatar shape="square" size={48} icon={<GiftOutlined />} />
                      );
                    },
                  },
                  {
                    title: t('orders.columns.product', 'Product'),
                    key: 'name',
                    render: (_: unknown, row: NonNullable<OfferDetails['products']>[number]) =>
                      isRtl ? row.name_ar || row.name_en || '—' : row.name_en || row.name_ar || '—',
                  },
                  {
                    title: t('orders.columns.quantity', 'Qty'),
                    key: 'quantity',
                    width: 90,
                    render: (_: unknown, row: NonNullable<OfferDetails['products']>[number]) =>
                      Number(row.pivot?.quantity ?? 0).toLocaleString(),
                  },
                  {
                    title: t('orders.columns.unitPrice', 'Unit price'),
                    key: 'unit_price',
                    render: (_: unknown, row: NonNullable<OfferDetails['products']>[number]) => (
                      <span>
                        {formatMoney(row.pivot?.unit_price)} {t('common.SAR', 'SAR')}
                      </span>
                    ),
                  },
                  {
                    title: t('products.columns.price', 'Selling price'),
                    key: 'selling_price',
                    render: (_: unknown, row: NonNullable<OfferDetails['products']>[number]) => (
                      <span>
                        {formatMoney(row.selling_price)} {t('common.SAR', 'SAR')}
                      </span>
                    ),
                  },
                  {
                    title: t('orders.createOrder.total', 'Line total'),
                    key: 'line_total',
                    render: (_: unknown, row: NonNullable<OfferDetails['products']>[number]) => {
                      const qty = Number(row.pivot?.quantity ?? 0);
                      const unitPrice = Number(row.pivot?.unit_price ?? 0);
                      return (
                        <span>
                          {formatMoney(qty * unitPrice)} {t('common.SAR', 'SAR')}
                        </span>
                      );
                    },
                  },
                ]}
              />

              <Title level={5} style={{ marginBottom: 10 }}>
                {t('offers.columns.customers', 'Target customers')} (
                {selectedOffer.customers?.length ?? 0})
              </Title>
              <AntTable
                dataSource={selectedOffer.customers || []}
                rowKey="id"
                pagination={false}
                size="small"
                scroll={{ x: 680 }}
                style={{ marginBottom: 16 }}
                columns={[
                  { title: t('customers.columns.id', 'ID'), dataIndex: 'id', key: 'id', width: 80 },
                  {
                    title: t('orders.columns.customer', 'Customer'),
                    dataIndex: 'name',
                    key: 'name',
                    render: (value: string | undefined) => value || '—',
                  },
                  {
                    title: t('orders.columns.phone', 'Phone'),
                    dataIndex: 'phone_number',
                    key: 'phone_number',
                    render: (value: string | undefined) => value || '—',
                  },
                  {
                    title: t('customers.columns.organization', 'Organization'),
                    dataIndex: 'organization',
                    key: 'organization',
                    render: (value: string | undefined) => value || '—',
                  },
                ]}
              />

              <Title level={5} style={{ marginBottom: 10 }}>
                {t('offers.columns.ordersCount', 'Linked orders')} (
                {selectedOffer.orders?.length ?? 0})
              </Title>
              <AntTable
                dataSource={selectedOffer.orders || []}
                rowKey="id"
                pagination={false}
                size="small"
                columns={[
                  { title: t('orders.columns.id', 'ID'), dataIndex: 'id', key: 'id', width: 80 },
                  {
                    title: t('orders.columns.reference', 'Reference'),
                    dataIndex: 'reference',
                    key: 'reference',
                    width: 150,
                  },
                  {
                    title: t('orders.columns.customer', 'Customer'),
                    key: 'customer',
                    render: (_: any, row: any) => row.customer?.name || '—',
                  },
                  {
                    title: t('orders.columns.phone', 'Phone'),
                    key: 'phone',
                    render: (_: any, row: any) => row.customer?.phone_number || '—',
                  },
                  {
                    title: t('orders.columns.status', 'Status'),
                    dataIndex: 'status',
                    key: 'status',
                  },
                  {
                    title: t('orders.columns.shippingStatus', 'Shipping status'),
                    dataIndex: 'shipping_status',
                    key: 'shipping_status',
                    render: (value: string | undefined) => value || '—',
                  },
                  {
                    title: t('orders.columns.createdAt', 'Created At'),
                    dataIndex: 'created_at',
                    key: 'created_at',
                    render: (value: string) => formatDateTime(value),
                  },
                  {
                    title: t('common.actions', 'Actions'),
                    key: 'actions',
                    width: 100,
                    render: (_: any, row: any) => (
                      <Button
                        type="link"
                        size="small"
                        onClick={() => {
                          setDetailsOpen(false);
                          history.push(`/orders/${row.id}`);
                        }}
                      >
                        {t('common.view', 'View')}
                      </Button>
                    ),
                  },
                ]}
              />
            </div>
          )}
        </Spin>
      </Modal>
    </AnimatedContainer>
  );
};

export default Offers;
